import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import BuyCreditsModal from "components/BuyCreditsModal";
import Tooltip from "components/Tooltip";
import { IconDesign, IconGhost, IconHelp, IconShared } from "icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import userService from "api/user/user.service";
import { IBonusCredit, ILivePlan } from "api/user/user.type";
import paymentService from "api/payment/payment.service";
import { IPaymentAddonsResponse } from "api/payment/payment.type";
import { useAppSelector, useAppDispatch } from "store";
import { DateFormat, formatDate, humanizePackage } from "utils";
import usePermissionState from "hooks/usePermissionState";
import { useSnackbar } from "hooks/useSnackbar";
import { useBillingPortal } from "hooks/useBillingPortal";
import { trackEvent } from "utils/analytics";
import { EVENTS } from "utils/analytics.events";
import ProgressBar from "components/ProgressBar";
import teamsService from "api/teams/teams.service";
import { setAccountSelectedMenu } from "store/commonSlice";
import { AccountSelectMenu } from "pages/Account/Account";
import developerService from "api/developer/developer.service";
import { hasPaymentProblem, resolvePlanStatus, selectPrimaryPlan } from "utils/planStatus";
import { httpStatusOf } from "utils/httpStatusOf";
import ContactAccountManagerModal from "components/WarningSnackbar/ContactAccountManagerModal";

const creditConfig: Record<
  string,
  {
    icon: React.ComponentType<any>;
    progressClassName: string;
    tooltipKey: string;
  }
> = {
  refabric_credits: {
    icon: IconDesign,
    progressClassName: "bg-green",
    tooltipKey: "account:manage_members.credit_tooltips.pool",
  },
  model_credits: {
    icon: IconGhost,
    progressClassName: "bg-purple",
    tooltipKey: "account:manage_members.credit_tooltips.model_credits",
  },
};

// Info-icon tooltip explaining a credit type — reuses the Manage Members credit tooltip copy.
const InfoTip = ({ tip }: { tip: string }) => (
  <Tooltip content={tip} position="top" contentClassName="!whitespace-normal max-w-[200px] leading-snug">
    <IconHelp className="w-3.5 h-3.5 text-grey cursor-help" />
  </Tooltip>
);

// bonus_credits[].expires_at is a space-separated implicit-UTC datetime ("YYYY-MM-DD HH:MM:SS").
// `new Date()` parses a non-ISO string as LOCAL time, which can render the expiry a calendar day
// early in negative-UTC zones. Normalize to an explicit-UTC instant first (no-op if it already
// carries a 'Z'/offset, e.g. the isoformat credits_expire_at field).
const parseBeUtc = (s: string): string => (/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : s.trim().replace(" ", "T") + "Z");

const PlanBilling = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { showSuccess } = useSnackbar();
  const { user } = useAppSelector((state) => state.common);
  // What the route-level `GET /users/{id}` already established (Redux, STRIPE Phase 3). Used when THIS screen's own read
  // fails, so a known payment problem never falls back to "View plans" → /pricing.
  const reduxHasPaymentProblem = useAppSelector((state) => state.common.user.hasPaymentProblem);
  const reduxCanManageBilling = useAppSelector((state) => state.common.user.canManageBilling);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPackage, setCurrentPackage] = useState<any>();
  const [credits, setCredits] = useState<any>();
  const [bonusCredits, setBonusCredits] = useState<IBonusCredit[]>([]);
  // STRIPE P6 F-d: the portal session is fetched on CLICK (hooks/useBillingPortal), never at page load.
  const { openPortal, isOpening: isOpeningPortal } = useBillingPortal();
  // The plan card stays hidden until the USER DETAIL lands — rendering before it would show "no active plan" to a paying
  // customer while `currentPackage` is still null.
  const [isLoadingUserDetail, setIsLoadingUserDetail] = useState<boolean>(true);
  const [addons, setAddons] = useState<IPaymentAddonsResponse | null>(null);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [webhookRefetchTick, setWebhookRefetchTick] = useState(0);
  const [seats, setSeats] = useState<{ used: number; limit: number } | null>(null);
  const [teamPlanName, setTeamPlanName] = useState<string>("");
  // D2 (STRIPE Phase 3): ANY plan row in past_due / unpaid / incomplete — not only the presented one.
  const [paymentProblem, setPaymentProblem] = useState(false);
  // The backend's billing-management verdict — the same predicate the portal route gates on.
  const [canManageBilling, setCanManageBilling] = useState(false);
  // True once THIS screen's `GET /users/{id}` succeeded — from then on its verdicts are authoritative and the route-level
  // Redux flags (which may be older) are no longer consulted.
  const [hasFreshDetail, setHasFreshDetail] = useState(false);
  // What the spend gate lets through now, per credit type (`GET /v1/account/balance` → `spendableOf`). Absent → unknown.
  const [spendable, setSpendable] = useState<Record<string, number>>({});
  // P7 F2-B6 → F2-F1: v3's double-plan verdict (`multiple_plans`, the F1 alert's own rule) and the plans it counts. The FE
  // never counts plans itself — a contract or demo beside one paid plan is not a duplicate.
  const [multiplePlans, setMultiplePlans] = useState(false);
  const [duplicatePlans, setDuplicatePlans] = useState<ILivePlan[]>([]);
  const [showAccountManager, setShowAccountManager] = useState(false);
  const addonSuccessHandled = React.useRef(false);

  const { isEnabled: isModelTrainingEnabled } = usePermissionState("model_training");

  const fetchUserDetail = React.useCallback(() => {
    // On the hard load back from Stripe this component mounts before tokenControl has
    // populated Redux — skip; the [user.id]-keyed effect below refetches once the id lands.
    if (!user.id) return;

    setIsLoadingUserDetail(true);
    userService
      .getUserDetail({ user_id: user.id })
      .then((res) => {
        // 401/403/409/503 resolve with responseData: null + _handled: true (CLAUDE.md Trap #1).
        if (res?._handled || !res?.responseData) return;

        const packageDetail = res.responseData.package_detail;
        const creditsData = res.responseData.credit_info;

        setCurrentPackage(selectPrimaryPlan(packageDetail, res.responseData.primary_subscription_id));
        setMultiplePlans(res.responseData.multiple_plans === true);
        setDuplicatePlans((res.responseData.live_plans ?? []).filter((plan) => plan.counts_as_duplicate === true));
        setPaymentProblem(hasPaymentProblem(packageDetail));
        setCanManageBilling(res.responseData.can_manage_billing === true);
        setHasFreshDetail(true);
        setCredits(creditsData);
        setBonusCredits(res.responseData.bonus_credits ?? []);
      })
      .catch((error) => {
        // Status only: the error's axios config carries the session bearer token (REF-6708, utils/httpStatusOf).
        console.error("Failed to fetch user details", { status: httpStatusOf(error) });
      })
      .finally(() => {
        setIsLoadingUserDetail(false);
      });
  }, [user.id]);

  React.useEffect(() => {
    fetchUserDetail();
  }, [fetchUserDetail]);

  // The spend gate's balance. A failure (or a stack without the route) leaves it unknown and the line hidden — the old
  // `total − used` figure is NOT what the gate allows (it ignores usage on lapsed buckets and the member ceiling).
  React.useEffect(() => {
    let cancelled = false;

    developerService
      .getBalance()
      .then((res) => {
        if (cancelled) return;
        // A handled (interceptor-owned) answer has no `balances` — the figure is unknown again, so the previous one is
        // withdrawn rather than left on screen as if current ([HTTP_HANDLED_RESPONSE]).
        if (!res || ("_handled" in res && res._handled) || !("balances" in res) || !Array.isArray(res.balances)) {
          setSpendable({});

          return;
        }
        setSpendable(Object.fromEntries(res.balances.filter((b) => typeof b.remaining === "number").map((b) => [b.credit_type, b.remaining])));
      })
      .catch(() => {
        // Unknown, not zero — and not the last known value either.
        if (!cancelled) setSpendable({});
      });

    return () => {
      cancelled = true;
    };
  }, [webhookRefetchTick]);

  React.useEffect(() => {
    paymentService
      .getAddons()
      .then((res) => {
        if (res?._handled || !res?.responseData) return;

        setAddons(res.responseData);
      })
      .catch(() => {
        // Environments whose BE doesn't ship the add-on endpoints yet (404) — keep the button hidden.
      });
  }, []);

  // Seats summary for the plan card. getRoster is admin-only (rejects 403 for non-admins) — degrade
  // silently and just hide the seats row when unavailable. Count mirrors the Manage Members header.
  React.useEffect(() => {
    let cancelled = false;

    teamsService
      .getRoster()
      .then((res) => {
        if (cancelled || res?._handled || !res?.responseData) return;

        const roster = res.responseData;
        setSeats({ used: roster.members.length + roster.pending_invites.length, limit: roster.seat_limit });
        // Fallback plan name only. The live package_detail[].name (currentPackage.name) is the source
        // of truth for the label — the team's denormalized package_type can lag a package transition
        // (REF-5831: it stayed "photoshoot_starter" after a customer upgraded to Design Studio, so the
        // paid plan never surfaced). This value is used only when package_detail carries no name.
        setTeamPlanName(humanizePackage(roster.team.package_type));
      })
      .catch(() => {
        // 403 for non-admins / no team — leave seats null so the row stays hidden.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Stripe one-time checkout returns to /account?addon_success={state_id} (REF-5450).
  React.useEffect(() => {
    const stateId = searchParams.get("addon_success");

    if (!stateId || addonSuccessHandled.current) return;
    addonSuccessHandled.current = true;

    trackEvent(EVENTS.BUY_CREDITS.PURCHASE_SUCCESS, { transaction_id: stateId });
    showSuccess(t("account:buy_credits.success"));

    const next = new URLSearchParams(searchParams);
    next.delete("addon_success");
    setSearchParams(next, { replace: true });

    // The BE credits the pool synchronously in the Stripe webhook; one delayed refetch
    // covers the rare case where the redirect back beats the webhook. The timer bumps a
    // tick instead of calling fetchUserDetail directly so the effect below runs the
    // LATEST callback (on the hard load back from Stripe, user.id is still empty here).
    // Deliberately no clearTimeout cleanup: StrictMode's simulated unmount would cancel
    // the one-shot timer for good (the ref guard blocks re-arming), and a post-unmount
    // setState is a safe no-op in React 18.
    setTimeout(() => setWebhookRefetchTick((tick) => tick + 1), 5000);
  }, [searchParams, setSearchParams, showSuccess, t]);

  React.useEffect(() => {
    if (webhookRefetchTick > 0) fetchUserDetail();
  }, [webhookRefetchTick, fetchUserDetail]);

  const canBuyCredits = Boolean(addons?.can_purchase && (addons?.packages?.length ?? 0) > 0);

  const handleOpenBuyCredits = () => {
    trackEvent(EVENTS.BUY_CREDITS.MODAL_OPEN);
    setShowBuyCredits(true);
  };

  const visibleCredits = useMemo(
    () =>
      credits
        ? Object.entries(credits).filter(([key, credit]: [string, any]) => {
            if ((credit?.total ?? 0) <= 0) return false;
            if (key === "refabric_credits") return true;
            if (key === "model_credits") return isModelTrainingEnabled;

            return false;
          })
        : [],
    [credits, isModelTrainingEnabled]
  );

  // D2: a payment problem anywhere wins; otherwise "ends on" for a scheduled cancellation, then trial / renews / active until.
  // This screen's verdicts once its own read succeeded; until then — or when that read failed — the route-level Redux ones.
  const effectivePaymentProblem = hasFreshDetail ? paymentProblem : reduxHasPaymentProblem;
  const effectiveCanManageBilling = hasFreshDetail ? canManageBilling : reduxCanManageBilling;
  const planStatus = effectivePaymentProblem ? ({ kind: "payment_problem" } as const) : resolvePlanStatus(currentPackage);
  const renewalText = (() => {
    switch (planStatus.kind) {
      case "payment_problem":
        return effectiveCanManageBilling ? t("account:plan_billing.payment_problem") : t("account:plan_billing.payment_problem_member");
      case "ended":
        return t("account:plan_billing.ended_on", { date: formatDate(planStatus.date, 1) });
      case "ends":
        return planStatus.reason === "contract"
          ? t("account:plan_billing.contract_ends_on", { date: formatDate(planStatus.date, 1) })
          : t("account:plan_billing.ends_on", { date: formatDate(planStatus.date, 1) });
      case "managed":
        return t("account:plan_billing.managed_until", { date: formatDate(planStatus.date, 1) });
      case "trial":
        // STRIPE P6 FE-1: a trial says so — and, when Stripe bills the plan after it, what the conversion charges.
        return planStatus.price === undefined
          ? t("account:plan_billing.trial_ends_on", { date: formatDate(planStatus.date, 1) })
          : t("account:plan_billing.trial_converts_paid", { date: formatDate(planStatus.date, 1), price: planStatus.price });
      case "changes":
        return t("account:plan_billing.changes_on", { date: formatDate(planStatus.date, 1) });
      case "renews":
        return t("account:plan_billing.renews_paid", { date: formatDate(planStatus.date, 1), price: planStatus.price });
      case "active_until":
        return t("account:plan_billing.active_until", { date: formatDate(planStatus.date, 1) });
      default:
        return t("account:plan_billing.no_plan_subtitle");
    }
  })();
  const isPaymentProblem = planStatus.kind === "payment_problem";
  // A Refabric-managed contract (K11) or a contract that ends: no self-serve portal or upgrade — the account manager is the
  // remedy (the existing AM modal, with plan wording).
  const isAccountManaged = planStatus.kind === "managed" || (planStatus.kind === "ends" && planStatus.reason === "contract");
  // A plan the customer canceled stays resumable in the Billing Portal (no in-app resume: K9 is open).
  const isUserCanceled = planStatus.kind === "ends" && planStatus.reason === "user_cancel" && effectiveCanManageBilling;
  const intervalLabel = (interval: string | null | undefined) => {
    if (interval === "monthly") return t("billing:pricing.monthly");
    if (interval === "yearly") return t("billing:pricing.yearly");

    return null;
  };

  return (
    <div className="flex flex-col gap-5 max-w-[720px]">
      {(currentPackage || !isLoadingUserDetail) && (
        <div className="flex flex-col rounded-md border border-black-300 bg-body-100 divide-y divide-black-300">
          {/* Plan header */}
          <div className="flex items-start justify-between gap-3 p-4">
            <div className="flex flex-col gap-1 min-w-0">
              <span className="text-bodySm text-grey">{t("account:plan_billing.current_plan_label")}</span>
              <span className="text-h3 text-trueBlack dark:text-white">
                {currentPackage ? currentPackage.name || teamPlanName || t("account:plan_billing.custom_plan_fallback") : t("account:plan_billing.no_active_plan")}
              </span>
              <span className={isPaymentProblem ? "text-bodySm text-orange" : "text-bodySm text-grey"} data-testid="plan-billing-status">
                {renewalText}
              </span>
              {multiplePlans && (
                // F2-F1: same text for manager and member; no global banner — ops gets the alert (F2-B4) and checks first.
                <div className="flex flex-col gap-1 mt-1" data-testid="plan-billing-multiple-plans">
                  <span className="text-bodySm text-orange">{t("account:plan_billing.multiple_plans")}</span>
                  {duplicatePlans.length > 0 && (
                    <ul className="list-disc pl-5 text-bodySm text-trueBlack dark:text-white">
                      {duplicatePlans.map((plan, index) => {
                        const interval = intervalLabel(plan.interval);

                        return <li key={plan.subscription_id ?? index}>{interval ? `${plan.name ?? ""} · ${interval}` : plan.name}</li>;
                      })}
                    </ul>
                  )}
                  <Button className="btn-secondary-small w-fit mt-1" onClick={() => dispatch(setAccountSelectedMenu(AccountSelectMenu.Support))} data-testid="plan-billing-contact-support">
                    {t("account:plan_billing.contact_support")}
                  </Button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2.5 flex-shrink-0">
              {isPaymentProblem ? (
                // D2: payment recovery is THE action (the existing Billing Portal) — no upgrade/purchase CTA beside it, so a
                // past_due customer is not steered into a second subscription. Members get the explanation only.
                effectiveCanManageBilling && (
                  <Button className="btn-primary-small" disabled={isOpeningPortal} onClick={openPortal} data-testid="plan-billing-update-payment">
                    {isOpeningPortal ? t("account:plan_billing.manage_billing_loading") : t("account:plan_billing.update_payment_method")}
                  </Button>
                )
              ) : isAccountManaged ? (
                <Button className="btn-primary-small" onClick={() => setShowAccountManager(true)} data-testid="plan-billing-contact-account-manager">
                  {t("billing:contact_account_manager.title")}
                </Button>
              ) : (
                <>
                  {currentPackage && (
                    <Button className="btn-secondary-small" disabled={isOpeningPortal} onClick={openPortal} data-testid="plan-billing-manage-billing">
                      {isOpeningPortal ? t("account:plan_billing.manage_billing_loading") : isUserCanceled ? t("account:plan_billing.resume_in_portal") : t("account:plan_billing.manage_billing")}
                    </Button>
                  )}
                  <Button className="btn-primary-small" onClick={() => navigate("/pricing")}>
                    {currentPackage ? t("account:plan_billing.upgrade_plan") : t("account:plan_billing.view_plans")}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Seats (admin-only; hidden when getRoster is unavailable) */}
          {seats && (
            <div className="flex items-center gap-3 p-4">
              <IconShared className="h-5 w-5 flex-shrink-0 text-trueBlack dark:text-white" />
              <div className="flex flex-1 flex-col min-w-0">
                <span className="text-bodyMd text-trueBlack dark:text-white">{t("account:plan_billing.seats_label")}</span>
                <span className="text-bodySm text-grey">{t("account:plan_billing.seats_count", { used: seats.used, limit: seats.limit })}</span>
              </div>
              <Button className="btn-secondary-small flex-shrink-0" onClick={() => dispatch(setAccountSelectedMenu(AccountSelectMenu.ManageMembers))}>
                {t("account:plan_billing.manage_members")}
              </Button>
            </div>
          )}

          {/* Credit rows */}
          {visibleCredits.map(([key, credit]: [string, any]) => {
            const config = creditConfig[key];
            const Icon = config.icon;
            const total = credit?.total ?? 0;
            const used = credit?.used ?? 0;
            const available = Math.max(total - used, 0);
            // Pre-aggregated by the BE (one row per action_code) — render as-is, no re-summing.
            const bonus = bonusCredits.find((item) => item.action_code === key && item.remaining > 0);

            return (
              <div key={key} className="flex items-start gap-3 p-4">
                <Icon className="h-5 w-5 flex-shrink-0 text-trueBlack dark:text-white" />
                <div className="flex flex-1 flex-col gap-1.5 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1 text-bodyMd text-trueBlack dark:text-white">
                      {credit?.title}
                      <InfoTip tip={t(config.tooltipKey)} />
                    </span>
                    <span className="text-bodySm text-grey whitespace-nowrap">
                      {t("account:plan_billing.available_label", { available: available.toLocaleString(), total: total.toLocaleString() })}
                    </span>
                  </div>
                  <ProgressBar maxAmount={total} currentAmount={used} progressBarClassName={config.progressClassName} trackClassName="bg-body-200" />
                  {/* The spend gate's own answer (team pool, member ceiling, extra credits included) — what an action can use now. */}
                  {spendable[key] !== undefined && (
                    <span className="text-bodySm text-trueBlack dark:text-white" data-testid={`plan-billing-available-now-${key}`}>
                      {t("account:plan_billing.available_now", { amount: spendable[key].toLocaleString() })}
                    </span>
                  )}
                  {bonus && (
                    <span className="text-bodySm text-green-dark dark:text-green">
                      {bonus.expires_at
                        ? t("account:plan_billing.bonus_credits_line", { amount: bonus.remaining, date: formatDate(parseBeUtc(bonus.expires_at), DateFormat.SHORT) })
                        : t("account:plan_billing.bonus_credits_line_no_date", { amount: bonus.remaining })}
                    </span>
                  )}
                  {/* REF-5549: non-admin team members see their own cap; flag who set it. */}
                  {credit?.is_member_cap && <span className="text-bodySm text-grey">{t("account:plan_billing.member_cap_note")}</span>}
                  {key === "refabric_credits" && canBuyCredits && (
                    <Button className="btn-primary-small w-fit mt-1" onClick={handleOpenBuyCredits}>
                      {t("account:plan_billing.buy_credits")}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {showAccountManager && <ContactAccountManagerModal onClose={() => setShowAccountManager(false)} description={t("billing:contact_account_manager.plan_description")} />}
      {addons && <BuyCreditsModal show={showBuyCredits} onClose={() => setShowBuyCredits(false)} addons={addons} />}
    </div>
  );
};

export default PlanBilling;
